import { useState, useEffect } from 'react'
import {Lock, LogOut, Download, Search, Trash2, X, ChevronDown, ChevronUp} from 'lucide-react'
import { lumi } from '@/lib/lumi'

// ──────────────────────────────────────────
// Tipos
// ──────────────────────────────────────────
type StatusType = 'Pendente' | 'Contatado' | 'Não qualificado' | 'Não respondeu' | 'Inscrito'

interface InscricaoSubmission {
  _id: string
  nome: string
  whatsapp: string
  email: string
  areasBloqueios: string
  processosAnteriores: string
  status: StatusType
  createdAt: string
  updatedAt: string
  [key: string]: unknown
}

// ──────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────
function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('pt-BR', {
      day: '2-digit', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

const STATUS_OPTIONS: StatusType[] = [
  'Pendente',
  'Contatado',
  'Não qualificado',
  'Não respondeu',
  'Inscrito',
]

const statusColors: Record<StatusType, string> = {
  'Pendente':          '#B88D56',
  'Contatado':         '#4F9FD1',
  'Não qualificado':   '#C75757',
  'Não respondeu':     '#8A7A96',
  'Inscrito':          '#5BAD6E',
}

// ──────────────────────────────────────────
// Componente
// ──────────────────────────────────────────
export default function AdminLeituraInscricao() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [loginData, setLoginData] = useState({ email: '', password: '' })
  const [loginError, setLoginError] = useState('')
  const [submissions, setSubmissions] = useState<InscricaoSubmission[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)

  // ── Login ──
  useEffect(() => {
    if (lumi.auth.isAuthenticated && lumi.auth.user?.userRole === 'ADMIN') {
      setIsAuthenticated(true)
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated) fetchSubmissions()
  }, [isAuthenticated])

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoginError('')
    try {
      const { user } = await lumi.auth.signInWithPassword({
        email: loginData.email,
        password: loginData.password,
      })
      if (user.userRole !== 'ADMIN') {
        setLoginError('Acesso negado. Apenas administradores podem acessar.')
        await lumi.auth.signOut()
        return
      }
      setIsAuthenticated(true)
    } catch {
      setLoginError('E-mail ou senha incorretos.')
    }
  }

  const handleLogout = async () => {
    await lumi.auth.signOut()
    setIsAuthenticated(false)
    setLoginData({ email: '', password: '' })
  }

  // ── Fetch ──
  const fetchSubmissions = async (opts?: { search?: string }) => {
    setLoading(true)
    try {
      const filter: Record<string, unknown> = {}
      if (opts?.search) {
        filter.$or = [
          { nome:     { $regex: opts.search, $options: 'i' } },
          { email:    { $regex: opts.search, $options: 'i' } },
          { whatsapp: { $regex: opts.search, $options: 'i' } },
        ]
      }
      const { list } = await lumi.entities.leitura_inscricao_submissions.list({
        filter,
        sort: { createdAt: -1 },
        limit: 100,
      })
      setSubmissions(list as InscricaoSubmission[])
    } catch {
      alert('Erro ao carregar respostas.')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = () => fetchSubmissions({ search: searchTerm })
  const handleClear  = () => { setSearchTerm(''); fetchSubmissions() }

  // ── Status (inline, sem expandir) ──
  const handleStatusChange = async (e: React.MouseEvent, id: string, newStatus: StatusType) => {
    e.stopPropagation()
    try {
      await lumi.entities.leitura_inscricao_submissions.update(id, {
        status: newStatus,
        updatedAt: new Date().toISOString(),
      })
      setSubmissions(prev => prev.map(s => s._id === id ? { ...s, status: newStatus } : s))
    } catch {
      alert('Erro ao atualizar status.')
    }
  }

  // ── Delete ──
  const handleDelete = async (e: React.MouseEvent, id: string, nome: string) => {
    e.stopPropagation()
    if (!confirm(`Tem certeza que deseja deletar o lead de ${nome}? Essa ação não pode ser desfeita.`)) return
    try {
      await lumi.entities.leitura_inscricao_submissions.delete(id)
      setSubmissions(prev => prev.filter(s => s._id !== id))
    } catch {
      alert('Erro ao excluir.')
    }
  }

  // ── Export CSV ──
  const exportCSV = () => {
    const headers = [
      'Nome completo', 'WhatsApp', 'E-mail',
      'Qual área da sua vida está com mais bloqueios agora?',
      'Você já fez algum processo de terapia, cura ou autoconhecimento antes? O que funcionou e o que ainda não mudou?',
      'Data de envio', 'Status',
    ]
    const rows = submissions.map(s => [
      s.nome, s.whatsapp, s.email,
      s.areasBloqueios,
      String(s.processosAnteriores || '').replace(/\n/g, ' '),
      formatDate(s.createdAt),
      s.status,
    ])
    const csv = [headers, ...rows]
      .map(r => r.map(v => `"${String(v ?? '').replace(/"/g, '""')}"`).join(','))
      .join('\n')
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href     = url
    a.download = `leitura-inscricao-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  // ──────────────────────────────────────────
  // Tela de login
  // ──────────────────────────────────────────
  if (!isAuthenticated) {
    return (
      <div style={{ minHeight: '100vh', background: '#0D0A12', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem' }}>
        <div style={{ background: '#18131F', border: '1px solid rgba(184,141,86,0.2)', borderRadius: 8, padding: '2.5rem', width: '100%', maxWidth: 420 }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(184,141,86,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem' }}>
            <Lock style={{ width: 22, height: 22, color: '#B88D56' }} />
          </div>
          <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 600, fontSize: '1.6rem', textAlign: 'center', marginBottom: '0.4rem' }}>
            Admin — Leitura do Campo Vibracional 2025
          </h1>
          <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(184,141,86,0.7)', fontSize: '0.9rem', textAlign: 'center', marginBottom: '2rem' }}>
            Área restrita · Apenas administradores
          </p>

          {loginError && (
            <div style={{ background: 'rgba(199,87,87,0.12)', border: '1px solid rgba(199,87,87,0.4)', borderRadius: 4, padding: '0.75rem 1rem', marginBottom: '1.5rem', fontFamily: 'Lora, Georgia, serif', color: '#E88', fontSize: '0.9rem', textAlign: 'center' }}>
              {loginError}
            </div>
          )}

          <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <label style={adminLabelStyle}>E-mail</label>
              <input type="email" required value={loginData.email} onChange={e => setLoginData({ ...loginData, email: e.target.value })} placeholder="admin@example.com" style={adminInputStyle} />
            </div>
            <div>
              <label style={adminLabelStyle}>Senha</label>
              <input type="password" required value={loginData.password} onChange={e => setLoginData({ ...loginData, password: e.target.value })} placeholder="••••••••" style={adminInputStyle} />
            </div>
            <button type="submit" style={adminBtnStyle}>Entrar</button>
          </form>
        </div>
      </div>
    )
  }

  // ──────────────────────────────────────────
  // Painel autenticado
  // ──────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: '#0D0A12' }}>
      {/* Header */}
      <div style={{ background: '#18131F', borderBottom: '1px solid rgba(184,141,86,0.15)', position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ maxWidth: 960, margin: '0 auto', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 600, fontSize: '1.4rem', marginBottom: 2 }}>
              Admin — Leitura do Campo Vibracional 2025
            </h1>
            <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', fontSize: '0.85rem' }}>
              {submissions.length} inscri{submissions.length !== 1 ? 'ções' : 'ção'} registrada{submissions.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button onClick={exportCSV} style={{ ...adminBtnSmallStyle, background: 'rgba(184,141,86,0.15)', border: '1px solid rgba(184,141,86,0.3)', color: '#B88D56' }}>
              <Download style={{ width: 14, height: 14 }} />
              Exportar CSV
            </button>
            <button onClick={handleLogout} style={{ ...adminBtnSmallStyle, background: 'rgba(199,87,87,0.15)', border: '1px solid rgba(199,87,87,0.3)', color: '#E88' }}>
              <LogOut style={{ width: 14, height: 14 }} />
              Sair
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 960, margin: '0 auto', padding: '2rem 1.5rem' }}>

        {/* Legenda de status */}
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {STATUS_OPTIONS.map(s => (
            <span key={s} style={{ fontFamily: 'Lora, Georgia, serif', fontSize: '0.75rem', padding: '3px 10px', borderRadius: 99, background: `${statusColors[s]}18`, color: statusColors[s], border: `1px solid ${statusColors[s]}44` }}>
              {s}
            </span>
          ))}
        </div>

        {/* Busca */}
        <div style={{ background: '#18131F', border: '1px solid rgba(184,141,86,0.15)', borderRadius: 6, padding: '1rem 1.25rem', marginBottom: '1.5rem', display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
            <Search style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', width: 16, height: 16, color: '#B88D56', opacity: 0.6 }} />
            <input
              type="text"
              placeholder="Buscar por nome, e-mail ou WhatsApp..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              style={{ ...adminInputStyle, paddingLeft: '2.5rem', width: '100%' }}
            />
          </div>
          <button onClick={handleSearch} style={adminBtnStyle}>Buscar</button>
          {searchTerm && (
            <button onClick={handleClear} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B88D56', display: 'flex', alignItems: 'center' }}>
              <X style={{ width: 16, height: 16 }} />
            </button>
          )}
        </div>

        {/* Lista */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <div style={{ width: 32, height: 32, border: '3px solid rgba(184,141,86,0.3)', borderTopColor: '#B88D56', borderRadius: '50%', margin: '0 auto 1rem', animation: 'spin 0.8s linear infinite' }} />
            <p style={{ fontFamily: 'Lora, Georgia, serif', color: '#B88D56', opacity: 0.7 }}>Carregando inscrições...</p>
          </div>
        ) : submissions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '4rem 0', background: '#18131F', borderRadius: 6, border: '1px solid rgba(184,141,86,0.12)' }}>
            <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(184,141,86,0.6)', fontSize: '1rem' }}>Nenhuma inscrição encontrada.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {submissions.map(sub => {
              const expanded = expandedId === sub._id
              const color    = statusColors[sub.status] ?? '#B88D56'
              return (
                <div key={sub._id} style={{ background: '#18131F', border: `1px solid ${expanded ? 'rgba(184,141,86,0.35)' : 'rgba(184,141,86,0.15)'}`, borderRadius: 6, overflow: 'hidden', transition: 'border-color 0.2s' }}>

                  {/* ── Cabeçalho do card (sempre visível) ── */}
                  <div style={{ padding: '1.25rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>

                    {/* Nome + data */}
                    <div
                      style={{ flex: 1, minWidth: 160, cursor: 'pointer' }}
                      onClick={() => setExpandedId(expanded ? null : sub._id)}
                    >
                      <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#FAFCF8', fontWeight: 600, fontSize: '1.2rem', marginBottom: '0.2rem' }}>
                        {sub.nome}
                      </h3>
                      <p style={{ fontFamily: 'Lora, Georgia, serif', color: 'rgba(184,141,86,0.65)', fontSize: '0.78rem' }}>
                        {formatDate(sub.createdAt)}
                      </p>
                    </div>

                    {/* Status inline editável + chevron + deletar */}
                    <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexShrink: 0, flexWrap: 'wrap' }}>
                      <select
                        value={sub.status}
                        onClick={e => e.stopPropagation()}
                        onChange={e => handleStatusChange(e as unknown as React.MouseEvent, sub._id, e.target.value as StatusType)}
                        style={{
                          background: `${color}18`,
                          border: `1px solid ${color}55`,
                          borderRadius: 99,
                          padding: '4px 12px 4px 10px',
                          fontFamily: 'Lora, Georgia, serif',
                          fontSize: '0.75rem',
                          color,
                          cursor: 'pointer',
                          outline: 'none',
                          appearance: 'none',
                          WebkitAppearance: 'none',
                          paddingRight: '1.8rem',
                          backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='${encodeURIComponent(color)}' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'right 8px center',
                          backgroundSize: '12px',
                        }}
                      >
                        {STATUS_OPTIONS.map(opt => (
                          <option key={opt} value={opt} style={{ background: '#18131F', color: '#FAFCF8' }}>{opt}</option>
                        ))}
                      </select>
                      <div
                        style={{ cursor: 'pointer', color: '#B88D56', opacity: 0.7, display: 'flex', alignItems: 'center' }}
                        onClick={() => setExpandedId(expanded ? null : sub._id)}
                      >
                        {expanded
                          ? <ChevronUp  style={{ width: 16, height: 16 }} />
                          : <ChevronDown style={{ width: 16, height: 16 }} />
                        }
                      </div>
                      <button
                        onClick={e => handleDelete(e, sub._id, sub.nome)}
                        title="Deletar lead"
                        style={{ background: 'rgba(199,87,87,0.12)', border: '1px solid rgba(199,87,87,0.35)', borderRadius: 4, padding: '5px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.35rem', color: '#E88', fontFamily: 'Lora, Georgia, serif', fontSize: '0.78rem' }}
                      >
                        <Trash2 style={{ width: 13, height: 13 }} />
                        Deletar
                      </button>
                    </div>
                  </div>

                  {/* ── Sumário sempre visível ── */}
                  <div style={{ padding: '0 1.5rem 1.25rem', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem 1.5rem' }}>
                    <div>
                      <p style={adminMetaLabel}>E-mail</p>
                      <p style={adminMetaValue}>{sub.email || '—'}</p>
                    </div>
                    <div>
                      <p style={adminMetaLabel}>WhatsApp</p>
                      <p style={adminMetaValue}>{sub.whatsapp || '—'}</p>
                    </div>
                    <div>
                      <p style={adminMetaLabel}>Qual área da sua vida está com mais bloqueios agora?</p>
                      <p style={adminMetaValue}>{sub.areasBloqueios || '—'}</p>
                    </div>
                  </div>

                  {/* ── Detalhes expandidos ── */}
                  {expanded && (
                    <div style={{ borderTop: '1px solid rgba(184,141,86,0.1)', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

                      <div>
                        <p style={adminMetaLabel}>1. Nome completo</p>
                        <p style={adminMetaValue}>{sub.nome || '—'}</p>
                      </div>
                      <div>
                        <p style={adminMetaLabel}>2. WhatsApp</p>
                        <p style={adminMetaValue}>{sub.whatsapp || '—'}</p>
                      </div>
                      <div>
                        <p style={adminMetaLabel}>3. E-mail</p>
                        <p style={adminMetaValue}>{sub.email || '—'}</p>
                      </div>
                      <div>
                        <p style={adminMetaLabel}>4. Qual área da sua vida está com mais bloqueios agora?</p>
                        <p style={adminMetaValue}>{sub.areasBloqueios || '—'}</p>
                      </div>
                      <div>
                        <p style={adminMetaLabel}>5. Você já fez algum processo de terapia, cura ou autoconhecimento antes? O que funcionou e o que ainda não mudou?</p>
                        <p style={{ ...adminMetaValue, whiteSpace: 'pre-wrap' }}>{sub.processosAnteriores || '—'}</p>
                      </div>
                      <div>
                        <p style={adminMetaLabel}>6. Data de envio</p>
                        <p style={adminMetaValue}>{formatDate(sub.createdAt)}</p>
                      </div>

                      {/* Ações */}
                      <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '0.75rem', borderTop: '1px solid rgba(184,141,86,0.08)' }}>
                        <button
                          onClick={e => handleDelete(e, sub._id, sub.nome)}
                          style={{ background: 'none', border: '1px solid rgba(199,87,87,0.3)', borderRadius: 4, padding: '0.5rem 1rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#C75757', fontFamily: 'Lora, Georgia, serif', fontSize: '0.85rem' }}
                        >
                          <Trash2 style={{ width: 14, height: 14 }} />
                          Excluir
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        select option { background: #18131F; color: #FAFCF8; }
      `}</style>
    </div>
  )
}

// ──────────────────────────────────────────
// Estilos inline
// ──────────────────────────────────────────
const adminLabelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'Lora, Georgia, serif',
  color: 'rgba(184,141,86,0.8)',
  fontSize: '0.85rem',
  marginBottom: '0.4rem',
}

const adminInputStyle: React.CSSProperties = {
  background: 'rgba(184,141,86,0.06)',
  border: '1px solid rgba(184,141,86,0.25)',
  borderRadius: 4,
  padding: '10px 14px',
  fontFamily: 'Lora, Georgia, serif',
  color: '#FAFCF8',
  fontSize: '0.95rem',
  outline: 'none',
  boxSizing: 'border-box',
}

const adminBtnStyle: React.CSSProperties = {
  background: '#C8923A',
  color: '#FAFCF8',
  fontFamily: 'Lora, Georgia, serif',
  fontWeight: 500,
  letterSpacing: '0.02em',
  border: 'none',
  cursor: 'pointer',
  padding: '10px 24px',
  borderRadius: 4,
  fontSize: '0.95rem',
}

const adminBtnSmallStyle: React.CSSProperties = {
  fontFamily: 'Lora, Georgia, serif',
  fontWeight: 500,
  cursor: 'pointer',
  padding: '7px 14px',
  borderRadius: 4,
  fontSize: '0.82rem',
  display: 'flex',
  alignItems: 'center',
  gap: '0.4rem',
}

const adminMetaLabel: React.CSSProperties = {
  fontFamily: 'Lora, Georgia, serif',
  color: 'rgba(184,141,86,0.6)',
  fontSize: '0.72rem',
  marginBottom: '0.25rem',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
}

const adminMetaValue: React.CSSProperties = {
  fontFamily: 'Lora, Georgia, serif',
  color: 'rgba(250,252,248,0.85)',
  fontSize: '0.9rem',
  lineHeight: 1.6,
}
